import { BaseWebComponentView } from '../../../../framework/BaseWebComponentView';
import { MAXIMUM_TASK_NAME_LENGTH, SubmitToSandboxPanelWebComponentSchema } from '../types';
import { GzFormFieldWebComponent } from '../../../../ui/gzForm/formField';
import { TemplateState } from '../../../../types/common/types';
import { translateFn } from '../../../../utils/translator.helper';
import { GzButtonWebComponent } from '../../../../ui/gzButton';
import {
  WEB_COMPONENT_SUBMIT_TO_SANDBOX_SAMPLES_FORM,
  WEB_COMPONENT_SUBMIT_TO_SANDBOX_SETTINGS_FORM,
} from '../../../../webcomponents.constants';
import { SubmitToSandboxSamplesFormWebComponent } from '../../common/submitToSandboxSamplesForm';
import { SubmitToSandboxSettingsFormWebComponent } from '../../common/submitToSandboxSettingsForm';
import { format } from 'date-fns';
import { GzClearableInputWebComponent } from '../../../../ui/gzInput';
import { combineLatest, Subscription } from 'rxjs';
import { RequiredValidator } from '../../../../ui/gzForm/validators/required.validator';
import { MaxLengthValidator } from '../../../../ui/gzForm/validators/max-length.validator';

export class SubmitToSandboxPanelView extends BaseWebComponentView {
  private validSubscription: Subscription | null = null;

  public dom: {
    $wrapper: HTMLElement;
    $targetFormField: GzFormFieldWebComponent<GzClearableInputWebComponent, string>;
    $taskNameFormField: GzFormFieldWebComponent<GzClearableInputWebComponent, string>;
    $samplesForm: SubmitToSandboxSamplesFormWebComponent;
    $settingsForm: SubmitToSandboxSettingsFormWebComponent;
    $saveButton: GzButtonWebComponent;
    $cancelButton: GzButtonWebComponent;
  };

  getDomElements() {
    this.dom = {
      $wrapper: this.shadow.querySelector('.wrapper'),
      $targetFormField: this.shadow.querySelector('#target-form-field'),
      $taskNameFormField: this.shadow.querySelector('#task-name-form-field'),
      $samplesForm: this.shadow.querySelector(WEB_COMPONENT_SUBMIT_TO_SANDBOX_SAMPLES_FORM),
      $settingsForm: this.shadow.querySelector(WEB_COMPONENT_SUBMIT_TO_SANDBOX_SETTINGS_FORM),
      $saveButton: this.shadow.querySelector('#save-button'),
      $cancelButton: this.shadow.querySelector('#cancel-button'),
    };

    this.setLoadingState();
    this.setTranslatedStrings();
  }

  public setLoadingState(): void {
    this.resetTemplateState(this.dom.$wrapper);
    this.setTemplateState(this.dom.$wrapper, TemplateState.LOADING);
  }

  public removeLoadingState(): void {
    this.resetTemplateState(this.dom.$wrapper);
  }

  setTranslatedStrings() {
    this.dom.$targetFormField.label = translateFn('Target');
    this.dom.$taskNameFormField.label = translateFn('Task name');

    this.dom.$saveButton.innerText = this.commonStrings.save;
    this.dom.$cancelButton.innerText = this.commonStrings.cancel;
  }

  buildSuccessView(data: SubmitToSandboxPanelWebComponentSchema) {
    this.dom.$targetFormField.value = data.targetName;
    this.dom.$targetFormField.readonly = true;

    this.buildTaskNameFormField();

    this.dom.$samplesForm.injectDependencies(this.dependencies);

    this.dom.$settingsForm.injectDependencies(this.dependencies);

    const startWebCompPromises: Promise<void>[] = [this.dom.$samplesForm.start(), this.dom.$settingsForm.start()];

    Promise.all(startWebCompPromises)
      .then(() => {
        this.removeLoadingState();
        this.setValidator();
      })
      .catch(console.error);
  }

  private buildTaskNameFormField(): void {
    this.dom.$taskNameFormField.value = translateFn('Submit to Sandbox Analyzer {date}', {
      date: format(new Date(), 'yyyy-MM-dd'),
    });

    this.dom.$taskNameFormField.Validators = [
      RequiredValidator(this.commonStrings.requiredField),
      MaxLengthValidator(
        MAXIMUM_TASK_NAME_LENGTH,
        translateFn('The task name must have a maximum of {maxLength} characters.', {
          maxLength: MAXIMUM_TASK_NAME_LENGTH,
        })
      ),
    ];
  }

  private setValidator(): void {
    this.validSubscription?.unsubscribe();
    this.validSubscription = combineLatest([
      this.dom.$taskNameFormField.valid$,
      this.dom.$samplesForm.valid$,
    ]).subscribe({
      next: ([taskNameFormFieldValid, samplesFormValid]) => {
        this.dom.$saveButton.disabled = !taskNameFormFieldValid || !samplesFormValid;
      },
    });
  }
}
